/* eslint-disable s4l/no-react-redux */
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useS4LContext } from '@s4l/react-context'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'

import Rooms from 'client/components/Rooms'
import { RoomsUpdater } from 'client/components/SearchForm'
import type { PropertyAPIResponse as PropertyType } from 'src/types/property'
import { usePage } from 'src/hooks/usePage'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { RelatedProperties, RoomViewSelector, type RoomView } from '@s4l/uikit'
import { getIsInternalUser } from 'client/contexts/Login/selectors'
import useExperiment from 'src/hooks/useExperimentConfig'
import { ROOM_CONFIGURATOR_EXPERIMENT } from 'src/constants/experimentConfig'

import styles from './styles.module.css'
import debug from 'debug'

const log = debug('s4l:stayforlong_com:RoomSection')

interface IRoomSection {
  readonly property: PropertyType
  readonly handleScroll: () => void
  readonly searchDataUpdated: boolean
  readonly source: string
  readonly isFetchingRooms: boolean
}

export default function RoomSection(props: IRoomSection) {
  const { property, handleScroll, searchDataUpdated, source, isFetchingRooms } = props
  const { i18n } = useS4LContext()
  const { pageId } = usePage()
  const { sendEvent: sendKafkaClick } = useKafkaEvent({ page: pageId })
  const { variant } = useExperiment(ROOM_CONFIGURATOR_EXPERIMENT.ID)
  const defaultView: RoomView =
    variant === ROOM_CONFIGURATOR_EXPERIMENT.NEW_DESIGN ? 'configurator' : 'list'
  const [selectedView, setSelectedView] = useState<RoomView>(defaultView)

  const previousIsFetchingRooms = useRef(isFetchingRooms)
  const isInternal = useSelector(getIsInternalUser)

  const {
    data: relatedProperties,
    loading: isApiLoading,
    error: relatedError,
    execute,
  } = useUseCase(
    'GetRelatedPropertyUseCase',
    {
      id: property.id.toString(),
    },
    { lazy: true }
  )

  if (relatedError) {
    log('Error on GetRelatedPropertyUseCase')
  }

  useEffect(() => {
    if (previousIsFetchingRooms.current === true && isFetchingRooms === false) {
      execute()
    }
    previousIsFetchingRooms.current = isFetchingRooms
  }, [isFetchingRooms]) // eslint-disable-line react-hooks/exhaustive-deps

  const showLoadingState = isFetchingRooms || isApiLoading

  function handleKafkaClickCTA(id: string, position: number) {
    sendKafkaClick({ section: SECTIONS.RELATED }, { id_property: parseInt(id), pos: position })
  }

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <h2 className={styles.title}>{i18n.formatMessage({ id: 'global.rooms-available' })}</h2>
        {isInternal && (
          <RoomViewSelector value={selectedView} onValueChange={value => setSelectedView(value)} />
        )}
      </div>
      <RoomsUpdater handleUpdate={handleScroll} />
      {searchDataUpdated && (
        <>
          <Rooms property={property} source={source} roomView={selectedView} />
          <RelatedProperties
            relatedPropertiesJSON={relatedProperties?.toJSON()}
            loading={showLoadingState}
            onClickProperty={handleKafkaClickCTA}
          />
        </>
      )}
    </div>
  )
}
